import React, { createContext, useContext, useState, useCallback } from 'react';
import ToastContainer from '../components/ToastContainer';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type: toast.type || 'info',
      title: toast.title,
      message: toast.message,
      autoHideDuration: toast.autoHideDuration !== undefined ? toast.autoHideDuration : 5000, // Default 5 seconds
      ...toast
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message, title = 'Success', options = {}) => {
    return showToast({ type: 'success', message, title, ...options });
  }, [showToast]);

  const showError = useCallback((message, title = 'Error', options = {}) => {
    return showToast({ type: 'error', message, title, ...options });
  }, [showToast]);

  const showWarning = useCallback((message, title = 'Warning', options = {}) => {
    return showToast({ type: 'warning', message, title, ...options });
  }, [showToast]);

  const showInfo = useCallback((message, title = 'Info', options = {}) => {
    return showToast({ type: 'info', message, title, ...options });
  }, [showToast]);

  const value = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};
